import { useAddPayrollPartner } from '@features/digitalOnboarding/hooks/useAddPayrollPartner';
import { useUpdatePayrollPartner } from '@features/digitalOnboarding/hooks/useUpdatePayrollPartner';
import { PayrollPartners } from '@features/digitalOnboarding/types';
import { yupResolver } from '@hookform/resolvers/yup';
import DoneIcon from '@mui/icons-material/Done';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { FC, useState, useEffect, useContext } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import isEmail from 'validator/lib/isEmail';
import * as yup from 'yup';
import CloseIcon from '@mui/icons-material/Close';
import { EditIcon, AddPlusIcon } from '../../../../vectors/index';
import { useUpdateCompanyPayroll } from '@features/digitalOnboarding/hooks/useUpdateCompanyPayroll';
import { authContext } from '@staffedupusa/shared-components';
import { selectedPlanName } from '@utils/pricing';
import { amplitudeEventsName } from '@utils/amplitudeEvents';
import { track } from '@amplitude/analytics-browser';
import { getStrippedText } from '@utils/helper';

type AddOnboardingPackageModalProps = {
  isOpen: boolean;
  selectedCompanyId?: number | null;
  handleClose: () => void;
  payrollData: PayrollPartners | null;
};

type FormData = {
  email: string;
  name: string;
  company_name: string;
};

const schema: yup.ObjectSchema<FormData> = yup
  .object({
    name: yup
      .string()
      .required('Name is required')
      .test('not-empty-html', 'Name cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    company_name: yup
      .string()
      .required('Company Name is required')
      .test('not-empty-html', 'Company Name cannot be empty', (value) => {
        if (!value) return false;
        return getStrippedText(value).length > 0;
      }),
    email: yup
      .string()
      .required('Email is required')
      .test('is-email', 'Must be a valid email', (value: string) =>
        isEmail(value),
      ),
  })
  .required();

const PayrollPartnerModal: FC<AddOnboardingPackageModalProps> = ({
  isOpen,
  selectedCompanyId,
  handleClose,
  payrollData,
}) => {
  const { authData } = useContext(authContext);
  const isCompany = authData?.user?.company;
  const selectedPlan = selectedPlanName(authData?.user?.company?.plan || 0);

  const [errorText, setErrorText] = useState<string>('');
  const [defaultValues, setDefaultValues] = useState<FormData>({
    email: '',
    name: '',
    company_name: '',
  });

  useEffect(() => {
    if (payrollData) {
      setDefaultValues({
        email: payrollData.onboarding_partner_email || '',
        name: payrollData.onboarding_partner_name || '',
        company_name: payrollData.onboarding_partner_company_name || '',
      });
    } else {
      setDefaultValues({
        email: '',
        name: '',
        company_name: '',
      });
    }
  }, [payrollData]);

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    mode: 'onTouched',
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const { addPayrollPartner, isAddingPayrollPartner } = useAddPayrollPartner({
    setErrorText,
  });
  const { updatePayrollPartner, isUpdatingPayrollPartner } =
    useUpdatePayrollPartner({
      setErrorText,
    });

  const { addUpdatePayrollPartners, isUpdatingPayrollPartner: isLoading } =
    useUpdateCompanyPayroll({
      setErrorText,
    });

  const onSubmit = (values: FormData) => {
    isCompany &&
      track(
        `${amplitudeEventsName.ONBOARDING_CLICK_SAVE_PAYROLL_PARTNER}-${selectedPlan}`,
      );
    const options = {
      onSuccess: (data: { status: number; message: string }) => {
        if (data.status === 200) {
          toast.success(data.message);
          reset();
          handleClose();
        }
      },
    };
    if (payrollData) {
      updatePayrollPartner(
        {
          ...values,
          ...(selectedCompanyId ? { company_id: selectedCompanyId } : {}),
          partner_id: payrollData.id,
        },
        options,
      );
    } else {
      if (selectedCompanyId) {
        addUpdatePayrollPartners(
          {
            ...values,
            company_id: selectedCompanyId,
          },
          options,
        );
      } else {
        addPayrollPartner(values, options);
      }
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        reset();
        handleClose();
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <CloseIcon />
        </IconButton>
        <br />
        {payrollData ? <EditIcon /> : <AddPlusIcon />}
        <br />
        <Typography variant="h4" mt={1}>
          {payrollData ? 'Update' : 'Add'} Payroll Partner
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ marginBottom: 3 }}>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack
              spacing={3}
              sx={{
                pt: 1,
                '& p': {
                  ml: 1,
                  fontSize: '15px',
                },
              }}
            >
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.email)}
                    helperText={errors?.email ? errors?.email.message : ''}
                  />
                )}
              />

              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.name)}
                    helperText={errors?.name ? errors?.name.message : ''}
                  />
                )}
              />

              <Controller
                control={control}
                name="company_name"
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Company Name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    error={Boolean(errors.company_name)}
                    helperText={
                      errors?.company_name ? errors?.company_name.message : ''
                    }
                  />
                )}
              />

              <LoadingButton
                type="submit"
                startIcon={<DoneIcon />}
                variant="contained"
                size="small"
                fullWidth
                loading={isAddingPayrollPartner || isUpdatingPayrollPartner}
                disabled={isAddingPayrollPartner || isUpdatingPayrollPartner}
                loadingPosition="start"
              >
                Save Payroll Partner
              </LoadingButton>
            </Stack>
          </form>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default PayrollPartnerModal;
